/**
 * Куда открыть папку по ссылке: `?cue=<реплика>` — на этой реплике (из плеера эфира: «Исправить в
 * редакторе ↗»); `?st=<мс от начала папки>` — на этом месте (его ведёт провайдер). Без React: разбор
 * адреса, поиск строки, расчёт прокрутки и стиль подсветки.
 */
import { AudioTrack } from "providers/AudioProvider/types";

export interface LinkTarget {
    /** Имя реплики на диске (original_id), без «@папка». */
    cue: string | null;
    /** Мс от начала папки; null — не задано. */
    st: number | null;
    /** Вкладку открыл плеер эфира (`from=player`). */
    fromPlayer: boolean;
}

/** «abc@7» → «abc»: так реплику зовёт плеер; редактору нужно имя на диске. */
export const normalizeCueId = (raw: string | null | undefined): string | null => {
    const value = String(raw ?? "").trim();
    if (!value) return null;
    const at = value.lastIndexOf("@");
    const name = (at > 0 ? value.slice(0, at) : value).trim();
    return name || null;
};

export const parseLinkTarget = (search: string): LinkTarget => {
    const params = new URLSearchParams(search || "");
    const st = Number(params.get("st"));
    return {
        cue: normalizeCueId(params.get("cue")),
        st: Number.isFinite(st) && st > 0 ? st : null,
        fromPlayer: params.get("from") === "player",
    };
};

/**
 * Строка реплики в папке: по имени на диске, по id строки («7_abc») или по имени с номером папки.
 * Сначала колонка оригинала (по ней строятся строки), потом перевода.
 */
export const findCueTrack = (
    lists: (AudioTrack[] | undefined)[],
    cue: string,
    folder: number,
): AudioTrack | undefined => {
    const withFolder = `${folder}_${cue}`;
    for (const list of lists) {
        const hit = (list || []).find(
            (one) => one && (one.original_id === cue || one.id === cue || one.id === withFolder),
        );
        if (hit) return hit;
    }
    return undefined;
};

/** Верх прокрутки, при котором строка стоит посередине колонки. */
export const centerScrollTop = (rowTop: number, rowHeight: number, viewHeight: number): number =>
    Math.max(0, Math.round(rowTop + rowHeight / 2 - viewHeight / 2));

export const CUE_TARGET_COLOR = "#C5F24A";

const attrValue = (value: string): string => value.replace(/["\\]/g, "\\$&").replace(/[\n\r]/g, " ");

/**
 * Подсветка реплики из ссылки — стилем по `data-cue-id`, а не классом на строке: строки
 * перерисовываются и пересоздаются при каждом перечитывании папки, а стиль переживает это.
 * Рамка 2px лаймом и 2 с пульса, затем ровный мягкий фон.
 */
export const cueHighlightCss = (rowId: string): string => {
    const sel = `[data-cue-id="${attrValue(rowId)}"]`;
    return `
${sel} {
    outline: 2px solid ${CUE_TARGET_COLOR} !important;
    outline-offset: -1px;
    background-color: rgba(197, 242, 74, 0.2) !important;
    animation: cueTargetPulse 0.5s ease-in-out 4;
}
@keyframes cueTargetPulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(197, 242, 74, 0); }
    50% { box-shadow: 0 0 0 5px rgba(197, 242, 74, 0.6); }
}
@media (prefers-reduced-motion: reduce) {
    ${sel} { animation: none; }
}
`;
};

/** Клик пришёлся в саму подсвеченную реплику (обе колонки) — подсветка остаётся. */
export const isInsideCue = (target: EventTarget | null, rowId: string): boolean => {
    let node = target as HTMLElement | null;
    while (node && node.nodeType === 1) {
        if (node.getAttribute && node.getAttribute("data-cue-id") === rowId) return true;
        node = node.parentElement;
    }
    return false;
};
